<template>
  <vue-final-modal
    :value="dialogVisible"
    name="rm-survey-dialog"
    :classes="['rm-survey-dialog__container']"
    :content-class="['rm-survey-dialog__content', 'v--modal-box', 'v--modal']"
    :click-to-close="true"
    :esc-to-close="true"
    :lock-scroll="true"
    attach="body"
    @input="onVisibleInput"
    @closed="onModalClosed"
  >
    <article v-if="survey && step && content" class="rm-survey-dialog">
      <header class="rm-survey-dialog__header">
        <img
          class="rm-survey-dialog__brand"
          src="/static/renew2_img/yagish_logo_20230522.png"
          alt="yagish"
        />
        <button
          type="button"
          class="rm-survey-dialog__close"
          aria-label="閉じる"
          @click="closeDialog"
        >
          ×
        </button>
      </header>

      <RmSurveyCompleted
        v-if="submitted"
        :content="content"
        @close="closeDialog"
      />

      <template v-else>
        <p v-if="showDownloadCompleted" class="rm-survey-dialog__downloaded">
          ✓ ダウンロードが完了しました
        </p>
        <p class="rm-survey-dialog__title">{{ content.title }}</p>
        <div class="rm-survey-dialog__progress">
          <div
            class="rm-survey-dialog__progress-track"
            role="progressbar"
            :aria-valuenow="step.number"
            aria-valuemin="1"
            :aria-valuemax="step.total"
          >
            <div
              class="rm-survey-dialog__progress-bar"
              :style="{ width: `${(step.number / step.total) * 100}%` }"
            />
          </div>
          <span class="rm-survey-dialog__progress-text"
            >{{ step.number }}/{{ step.total }}</span
          >
        </div>
        <p class="rm-survey-dialog__question">
          {{ step.question.questionText }}
        </p>
        <p
          v-if="step.question.answerType === 'multiple'"
          class="rm-survey-dialog__hint"
        >
          複数選択できます
        </p>
        <RmSurveyOptionList
          :question="step.question"
          :selected="currentSelected"
          @select="onSelect"
        />
        <input
          v-if="needsFreeText(step.question, currentSelected)"
          v-model="freeTexts[step.question.questionCode]"
          class="rm-survey-dialog__free-text"
          type="text"
          :maxlength="RM_FREE_TEXT_MAX_LENGTH"
          placeholder="具体的に教えてください（任意）"
        />
        <p v-if="errorMessage" class="rm-survey-dialog__error">
          {{ errorMessage }}
        </p>
        <button
          type="button"
          class="rm-survey-dialog__primary"
          :disabled="currentSelected.length === 0 || submitting"
          @click="onNext"
        >
          {{ step.isLast ? content.submitLabel : content.nextLabel }}
        </button>
        <p class="rm-survey-dialog__notice">
          {{ content.notice
          }}<a
            :href="content.privacyUrl"
            target="_blank"
            rel="noopener noreferrer"
            >プライバシーポリシー</a
          >
        </p>
        <button
          type="button"
          class="rm-survey-dialog__text"
          @click="closeDialog"
        >
          {{ content.closeLabel }}
        </button>
      </template>
    </article>
  </vue-final-modal>
</template>

<script setup lang="ts">
import { computed, onBeforeMount, onBeforeUnmount, ref } from 'vue'
import {
  emitRtDialogClosedWithoutSubmit,
  emitter,
  FORCE_HIDE_RT_DIALOGS
} from '@/plugins/emitter'
import RmSurveyCompleted from '@/components/realtime-transfer/RmSurveyCompleted.vue'
import RmSurveyOptionList from '@/components/realtime-transfer/RmSurveyOptionList.vue'
import { useAuth } from '@/composables/useAuth'
import { useSite } from '@/composables/useSite'
import { postRmAnswer, postRmDisplayLog } from '@/services/rm-survey-client'
import {
  RM_FREE_TEXT_MAX_LENGTH,
  rmSurveySubmitSchema
} from '@/validation/rmSurveyAnswer'
import {
  RM_SURVEY_DIALOG_TYPE,
  buildAnswers,
  needsFreeText,
  parseRmSurvey,
  resolveDeviceFlag,
  surveyStep,
  toggleSelection,
  type RmSelections,
  type RmSurvey
} from '@/utils/realtime-transfer/rmSurvey'

const SEND_ERROR = '送信できませんでした。時間をおいてお試しください'

const { user } = useAuth()
const { appFlag } = useSite()

const dialogVisible = ref(false)
const survey = ref<RmSurvey | null>(null)
const stepIndex = ref(0)
const selections = ref<RmSelections>({})
const freeTexts = ref<Record<string, string>>({})
const submitting = ref(false)
const submitted = ref(false)
const errorMessage = ref('')
const showDownloadCompleted = ref(true)
const formatId = ref('')
const rtConditionId = ref<number | null>(null)
const displayHistoryId = ref<number | null>(null)
const deviceFlag = resolveDeviceFlag(
  typeof navigator === 'undefined' ? '' : navigator.userAgent
)

const content = computed(() => survey.value?.dialogContent)
const step = computed(() =>
  survey.value
    ? surveyStep(survey.value, selections.value, stepIndex.value)
    : null
)
const currentSelected = computed(() =>
  step.value ? selections.value[step.value.question.questionCode] || [] : []
)

function onSelect(code: string) {
  if (!step.value) return
  const question = step.value.question
  selections.value = {
    ...selections.value,
    [question.questionCode]: toggleSelection(
      question,
      currentSelected.value,
      code
    )
  }
  errorMessage.value = ''
}

function onNext() {
  if (!step.value || currentSelected.value.length === 0) return
  if (step.value.isLast) {
    submit()
    return
  }
  stepIndex.value += 1
}

function onOpen(argList: unknown) {
  const args = Array.isArray(argList) ? argList : []
  const dialogInfo = (args[3] || {}) as Record<string, unknown>
  const parsed = parseRmSurvey(dialogInfo.rmSurvey)
  if (!parsed) {
    // 中身が壊れている RM 行は出さずに次の RT へ進める
    emitRtDialogClosedWithoutSubmit()
    return
  }
  survey.value = parsed
  stepIndex.value = 0
  selections.value = {}
  // Vue 2.7 は後から足したキーを検知しないので、全設問のキーを先に作る
  freeTexts.value = Object.fromEntries(
    parsed.questions.map((q) => [q.questionCode, ''])
  )
  submitted.value = false
  submitting.value = false
  errorMessage.value = ''
  showDownloadCompleted.value = dialogInfo.showDownloadCompletedTitle !== false
  formatId.value = typeof args[0] === 'string' ? args[0] : ''
  rtConditionId.value =
    typeof dialogInfo.conditionId === 'number' ? dialogInfo.conditionId : null
  displayHistoryId.value = null
  dialogVisible.value = true
  logDisplay(parsed.rmConditionId)
}

function logDisplay(rmConditionId: number) {
  const uuid = user.value?.uid
  if (!uuid) return
  postRmDisplayLog({
    uuid,
    rm_condition_id: rmConditionId,
    rt_condition_id: rtConditionId.value,
    format_id: formatId.value,
    device_flag: deviceFlag,
    app_flag: appFlag.value,
    site_flag: 'r'
  })
    .then((id) => {
      displayHistoryId.value = id
    })
    .catch((err) => console.error(err))
}

async function submit() {
  if (!survey.value || submitting.value) return
  const parsed = rmSurveySubmitSchema.safeParse({
    uuid: user.value?.uid ?? '',
    rm_condition_id: survey.value.rmConditionId,
    rm_display_history_id: displayHistoryId.value,
    format_id: formatId.value,
    device_flag: deviceFlag,
    app_flag: appFlag.value,
    site_flag: 'r',
    answers: buildAnswers(survey.value, selections.value, freeTexts.value)
  })
  if (!parsed.success) {
    errorMessage.value =
      parsed.error.issues[0]?.message || '回答を確認してください'
    return
  }
  submitting.value = true
  try {
    if (await postRmAnswer(parsed.data)) {
      submitted.value = true
    } else {
      errorMessage.value = SEND_ERROR
    }
  } catch (err) {
    console.error(err)
    errorMessage.value = SEND_ERROR
  } finally {
    submitting.value = false
  }
}

function onVisibleInput(next: unknown) {
  dialogVisible.value = next === true
}

function closeDialog() {
  dialogVisible.value = false
}

function onModalClosed() {
  // 回答したかどうかに関係なく、ワークゲート等の次の RT へ進める（Workgate / ヤギバディと同じ）
  emitRtDialogClosedWithoutSubmit()
}

onBeforeMount(() => {
  emitter.on(RM_SURVEY_DIALOG_TYPE, onOpen)
  emitter.on(FORCE_HIDE_RT_DIALOGS, closeDialog)
})
onBeforeUnmount(() => {
  emitter.off(RM_SURVEY_DIALOG_TYPE, onOpen)
  emitter.off(FORCE_HIDE_RT_DIALOGS, closeDialog)
})
</script>

<style scoped lang="scss">
$green: #0f9934;
$text: #2b2b2b;
$sub: #7a7a7a;
$line: #dcdcdc;

/*
 * 外側（暗幕・中央寄せ）だけは WorkgateDialog と同じ作法にする。
 * vue-final-modal の container / content は内部のノードなので、scoped だけでは
 * 届かず :deep() が要る。共有の `.v--modal-box.v--modal` が付ける余白・全幅は
 * 打ち消し、カードの見た目は中身の .rm-survey-dialog が持つ（YagiBuddy と同じ）。
 */
:deep(.rm-survey-dialog__container) {
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  padding: 20px 12px;
  overflow-y: auto;
}

:deep(.rm-survey-dialog__content.v--modal-box.v--modal) {
  width: auto;
  max-width: none;
  height: auto;
  padding: 0;
  background: transparent;
  box-shadow: none;
}

.rm-survey-dialog {
  box-sizing: border-box;
  width: min(calc(100vw - 1.5rem), 28rem);
  max-height: calc(100vh - 2rem);
  padding: 1.6rem 1.8rem 1.4rem;
  overflow-y: auto;
  color: $text;
  background: #fff;
  border-radius: 1.4rem;
  box-shadow: 0 18px 44px rgb(0 0 0 / 18%);
}

.rm-survey-dialog__header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 1.6rem;
}

.rm-survey-dialog__brand {
  display: block;
  width: auto;
  height: 2.4rem;
}

.rm-survey-dialog__close {
  padding: 0;
  font-size: 1.6rem;
  line-height: 1;
  color: $sub;
  background: transparent;
  border: 0;
  cursor: pointer;
}

.rm-survey-dialog__downloaded {
  margin: 0 0 0.9rem;
  font-size: 0.95rem;
  color: $green;
}

.rm-survey-dialog__title {
  margin: 0 0 1.2rem;
  font-size: 1.2rem;
  font-weight: 700;
}

.rm-survey-dialog__progress {
  display: flex;
  gap: 1.8rem;
  align-items: center;
  margin-bottom: 1.4rem;
}

.rm-survey-dialog__progress-track {
  flex: 1;
  height: 0.6rem;
  overflow: hidden;
  background: #e3e9df;
  border-radius: 999px;
}

.rm-survey-dialog__progress-bar {
  height: 100%;
  background: $green;
  border-radius: 999px;
  transition: width 0.2s ease;
}

.rm-survey-dialog__progress-text {
  font-size: 0.95rem;
  color: $sub;
}

.rm-survey-dialog__question {
  margin: 0 0 0.4rem;
  font-size: 1.2rem;
  font-weight: 700;
  line-height: 1.5;
}

.rm-survey-dialog__hint {
  margin: 0 0 0.4rem;
  font-size: 0.8rem;
  color: $sub;
}

.rm-survey-dialog__free-text {
  box-sizing: border-box;
  width: 100%;
  margin-top: 0.6rem;
  padding: 0.6rem 0.8rem;
  border: 1px solid $line;
  border-radius: 0.5rem;
}

.rm-survey-dialog__error {
  margin: 0.8rem 0 0;
  font-size: 0.85rem;
  color: #d32f2f;
}

.rm-survey-dialog__primary {
  display: block;
  box-sizing: border-box;
  width: 100%;
  margin-top: 1.4rem;
  padding: 0.95rem;
  font-size: 1rem;
  font-weight: 700;
  color: #fff;
  text-align: center;
  text-decoration: none;
  background: $green;
  border: 0;
  border-radius: 0.6rem;
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
    opacity: 0.4;
  }
}

.rm-survey-dialog__notice {
  margin: 1rem 0 0;
  font-size: 0.8rem;
  line-height: 1.6;
  color: $sub;
  text-align: center;

  a {
    color: $sub;
  }
}

.rm-survey-dialog__text {
  display: block;
  margin: 0.8rem auto 0;
  padding: 0.4rem;
  font-size: 0.95rem;
  color: $sub;
  background: transparent;
  border: 0;
  cursor: pointer;
}
</style>
